import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { checkPolicy } from "@/lib/api";
import ToolLayout from "@/components/ToolLayout";
import PostResultCTA from "@/components/PostResultCTA";
import useSEO from "@/hooks/useSEO";

const RISK_CONFIG = {
  CLEAR: {
    label: "Clear",
    icon: CheckCircle2,
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    desc: "No policy violations detected. This artifact is ready for review.",
  },
  REVIEW_NEEDED: {
    label: "Review needed",
    icon: AlertTriangle,
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    desc: "One or more issues require attention before this artifact can be approved.",
  },
  BLOCKED: {
    label: "Blocked",
    icon: XCircle,
    color: "text-rose-700",
    bg: "bg-rose-50 border-rose-200",
    desc: "Critical policy violations detected. This artifact must be revised before proceeding.",
  },
};

const SEVERITY_STYLES = {
  HIGH: "bg-rose-100 text-rose-700 border-rose-200",
  MEDIUM: "bg-amber-100 text-amber-700 border-amber-200",
  LOW: "bg-sky-100 text-sky-700 border-sky-200",
};

const EXAMPLE = `WHEN a payment transaction fails AFTER successful 3DS authentication AND the failure reason is 'acquirer_decline', THEN route the case to Tier 2 Payments. The agent must acknowledge the customer using John's standard phrasing from the EU handbook. Attach the customer's full card number to the escalation record for Finance.`;

export default function PolicyChecker() {
  useSEO({
    title: "Knowledge Artifact Policy Checker — Free Tool — ProofMark",
    description:
      "Paste any draft operational rule or knowledge artifact and get AI-powered policy flags for named-person phrasing, PII, scope ambiguity, and compliance conflicts.",
    path: "/tools/policy-checker",
  });

  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await checkPolicy(text);
      setResult(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 503) {
        setError("AI features are not available in this environment. Configure ANTHROPIC_API_KEY to enable policy checking.");
      } else {
        setError(detail || "Policy check failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const risk = result ? RISK_CONFIG[result.overall_risk] || RISK_CONFIG.REVIEW_NEEDED : null;

  return (
    <ToolLayout>
      <div className="mb-8">
        <span className="tracked-caps">Free Tool</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">
          Knowledge Artifact Policy Checker
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          Paste any draft operational rule, escalation path, or decision tree. Get AI-powered flags
          for named-person phrasing, PII exposure, scope ambiguity, attribution issues, and
          compliance conflicts.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 mb-8">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium">
              Paste your artifact text <span className="text-rose-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setText(EXAMPLE)}
              className="text-xs text-muted-foreground hover:text-brand transition-colors"
            >
              Load example
            </button>
          </div>
          <textarea
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste an escalation rule, decision tree, macro, or KB update draft…"
            rows={8}
            className="w-full rounded-md border border-border bg-card px-3 py-2.5 text-sm font-mono resize-y focus:outline-none focus:ring-1 focus:ring-brand"
          />
          <p className="text-xs text-muted-foreground mt-1">{text.length} characters</p>
        </div>

        <button
          type="submit"
          disabled={loading || !text.trim()}
          className="bg-brand text-brand-foreground px-6 py-2.5 rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Analysing…
            </>
          ) : (
            "Check for policy issues →"
          )}
        </button>

        {error && <p className="text-sm text-rose-600">{error}</p>}
      </form>

      {/* Results */}
      {result && risk && (
        <div className="space-y-5">
          {/* Overall risk */}
          <div className={`border rounded-lg p-5 ${risk.bg}`}>
            <div className="flex items-center gap-3 mb-1">
              <risk.icon className={`h-5 w-5 ${risk.color}`} />
              <span className={`font-medium ${risk.color}`}>{risk.label}</span>
            </div>
            <p className="text-sm text-muted-foreground ml-8">{risk.desc}</p>
          </div>

          {/* Flag list */}
          {result.flags.length > 0 ? (
            <div className="space-y-3">
              {result.flags.map((f, i) => (
                <div key={i} className="border border-border rounded-lg p-5 bg-card">
                  <div className="flex items-start gap-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded border shrink-0 mt-0.5 ${
                        SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.LOW
                      }`}
                    >
                      {f.severity}
                    </span>
                    <div>
                      <p className="font-medium text-sm">{f.issue}</p>
                      {f.suggestion && (
                        <p className="text-sm text-muted-foreground mt-1">{f.suggestion}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No specific flags — see overall result above.</p>
          )}

          {/* CTA */}
          <PostResultCTA
            openLoop={`That was one artifact. ProofMark flags policy violations across your entire knowledge base — automatically. See what the rest of your stack looks like.`}
            source="policy-checker"
            nextPath="/tools/contributor-class-classifier"
            nextLabel="Next: classify your knowledge sources →"
            testimonial={null}
          />

          <button
            onClick={() => { setText(""); setResult(null); }}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Check another artifact
          </button>
        </div>
      )}

      {/* Educational content */}
      <div className="mt-20 space-y-10 border-t border-border/80 pt-12">
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            Why knowledge artifacts need policy review before AI deployment
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            An AI system that follows a rule containing a person&apos;s name may be replicating that
            person&apos;s reasoning without attribution — a named-person phrasing issue. A rule that
            attaches a customer&apos;s card number to an escalation record embeds PII into a
            knowledge artifact that may later be used to train or prompt AI systems. Policy review
            catches these issues before they become compliance liabilities.
          </p>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            Common policy violations in enterprise knowledge artifacts
          </h2>
          <div className="space-y-3 max-w-3xl">
            {[
              {
                flag: "Named-person phrasing",
                desc: "Rules that reference or replicate a specific individual's voice or decision-making style. Creates attribution and voice-mimicry risks.",
              },
              {
                flag: "PII exposure",
                desc: "Customer identifiers, account numbers, or contact details embedded in operational rules. GDPR and PCI-DSS surface area.",
              },
              {
                flag: "Scope ambiguity",
                desc: "Vague qualifiers ('always', 'never', 'all customers') without clear conditions. Creates inconsistent AI behaviour.",
              },
              {
                flag: "Attribution issues",
                desc: "Content sourced from external or unverified third parties without documented rights to use.",
              },
              {
                flag: "Compliance conflicts",
                desc: "Contradictions with known regulatory requirements — particularly in regulated industries like finance, healthcare, and legal.",
              },
            ].map((item) => (
              <div key={item.flag} className="border border-border rounded-lg p-4 bg-card">
                <p className="font-medium text-sm mb-1">{item.flag}</p>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">The ProofMark policy flag taxonomy</h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            ProofMark maps policy flags to a formal taxonomy with severity levels (HIGH, MEDIUM,
            LOW) and links each flag to the specific policy rule that triggered it. Every flag is
            logged in the evidence record for the artifact, creating an auditable trail from
            flag detection through reviewer decision to final disposition.
          </p>
        </section>
      </div>
    </ToolLayout>
  );
}
